import { Injectable } from '@angular/core';
import { Observable, of, OperatorFunction } from 'rxjs';
import {
    bufferCount,
    catchError,
    concatMap,
    map,
    mergeMap
} from 'rxjs/operators';
import {
    PHOTO_VALIDATION_ERROR_FILE_SIZE,
    PHOTO_VALIDATION_ERROR_IMAGE_DIMENSIONS,
    PHOTO_VALIDATION_ERROR_IMAGE_LOAD_ERROR,
    PHOTO_VALIDATION_ERROR_IMAGE_SQUARE_DIMENSIONS,
    PHOTO_VALIDATION_ERROR_MIME_TYPE
} from '../../constants/photo.constants';
import { ValidatedPhoto } from '../../models/validated-photo';
import { ValidationError } from '../../models/validation-error';

import { PhotoLoaderService } from '../loader/photo-loader.service';

@Injectable()
export class PhotoValidationService {
    constructor(private _photoLoader: PhotoLoaderService) {}

    public validatePhotoFiles(
        acceptedMimeTypes: string[],
        maxFileSizeInBytes: number,
        minPhotoDimension: number,
        loadTimeout: number,
        squareDimensionCheck: boolean
    ): OperatorFunction<File[], ValidatedPhoto[]> {
        return (source: Observable<File[]>) => {
            // This probably requires a bit of explanation:
            // 1. We get an array of File objects that we need to validate
            // 2. Since validation is asynchronous (meh), we convert the array into an observable of Files
            // 3. Then we pipe each file through the validation function
            // 4. When we are done with the validation we need to turn the validation results back into an array
            //    The size of the array needs to be exactly the same as the size of the input array
            return source.pipe(
                concatMap((files) =>
                    of(...files).pipe(
                        this.validatePhotoFile(
                            acceptedMimeTypes,
                            maxFileSizeInBytes,
                            minPhotoDimension,
                            loadTimeout,
                            squareDimensionCheck
                        ),
                        bufferCount(files.length)
                    )
                )
            );
        };
    }

    private validatePhotoFile(
        acceptedMimeTypes: string[],
        maxFileSizeInBytes: number,
        minPhotoDimension: number,
        loadTimeout: number,
        squareDimensionCheck: boolean
    ): OperatorFunction<File, ValidatedPhoto> {
        return (source: Observable<File>) => {
            return source.pipe(
                map((file) => this.createValidationModel(file)),
                map((file) =>
                    this.conditionallyValidate(file, (f) =>
                        this.validateMimeType(f, acceptedMimeTypes)
                    )
                ),
                map((file) =>
                    this.conditionallyValidate(file, (f) =>
                        this.validateFileSize(f, maxFileSizeInBytes)
                    )
                ),
                mergeMap((file) => this.loadImage$(file, loadTimeout)),
                map((file) =>
                    this.conditionallyValidate(file, (f) =>
                        this.validateDimensions(f, minPhotoDimension, squareDimensionCheck)
                    )
                )
            );
        };
    }

    private createValidationModel(file: File): ValidatedPhoto {
        return new ValidatedPhoto(file);
    }

    private conditionallyValidate(
        file: ValidatedPhoto,
        validationFn: (file: ValidatedPhoto) => ValidatedPhoto
    ): ValidatedPhoto {
        if (file.hasValidationError) {
            return file;
        }
        return validationFn(file);
    }

    private loadImage$(
        file: ValidatedPhoto,
        loadTimeout: number
    ): Observable<ValidatedPhoto> {
        if (file.hasValidationError) {
            return of(file);
        }

        return this._photoLoader
            .loadImageFromFile(file.nativeFile, loadTimeout)
            .pipe(
                map((image: HTMLImageElement) =>
                    file.withDimensions(image.naturalWidth, image.naturalHeight)
                ),
                catchError(() =>
                    of(
                        file.withValidationError(
                            new ValidationError(
                                PHOTO_VALIDATION_ERROR_IMAGE_LOAD_ERROR
                            )
                        )
                    )
                )
            );
    }

    private validateMimeType(
        file: ValidatedPhoto,
        acceptedMimeTypes: string[]
    ): ValidatedPhoto {
        const isAcceptedMimeType = acceptedMimeTypes.includes(
            file.nativeFile.type
        );

        if (isAcceptedMimeType) {
            return file;
        }

        return file.withValidationError(
            new ValidationError(PHOTO_VALIDATION_ERROR_MIME_TYPE)
        );
    }

    private validateFileSize(
        file: ValidatedPhoto,
        maxFileSize: number
    ): ValidatedPhoto {
        const isAcceptedFileSize = file.nativeFile.size <= maxFileSize;

        if (isAcceptedFileSize) {
            return file;
        }

        return file.withValidationError(
            new ValidationError(PHOTO_VALIDATION_ERROR_FILE_SIZE)
        );
    }

    private validateDimensions(file: ValidatedPhoto, minDimension: number, squareDimensionCheck: boolean) {
        if (squareDimensionCheck && file.width !== file.height) {
            return file.withValidationError(
                new ValidationError(PHOTO_VALIDATION_ERROR_IMAGE_SQUARE_DIMENSIONS)
            );
        }
        const dimensionsTooSmall =
            Math.max(file.width, file.height) < minDimension;

        if (dimensionsTooSmall) {
            return file.withValidationError(
                new ValidationError(PHOTO_VALIDATION_ERROR_IMAGE_DIMENSIONS)
            );
        }

        return file;
    }
}
